import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { identifyUser } from '@/helpers';
import checkLocalStorageForODTP from '@/helpers/checkLocalStorageForODTP';
import { getUserQueryOptions, setPendingOdtpParam } from '@/queries/users';
import { setSessionError, setUserData, unsetUserData } from '@/redux/slices/user/actions';
import { buildURL, patch } from '@/shared/fetch';
import { getClientId } from '@/shared/ga';
import { trackMP } from '@/shared/mp';

/**
 * Resolve who is signed in, and keep it resolved. Mount once per entry point.
 *
 * One react-query query owns the fetching and the caching; redux keeps a copy because most of the
 * app reads it from there. The copy is derived — every write here comes from an answer the query
 * received, never the other way round.
 *
 * This replaced three overlapping mechanisms: a hook that stopped asking once the session was
 * checked, a raw `fetch` in `guardHelpers`, and a separate cross-tab syncer. Two of them observed
 * the same cache entry with different options, and all three wrote the same redux keys.
 *
 * Cache settings follow `AppInitializer` in the educators app: the user is wanted for the whole
 * life of the app, so it never expires on a timer, and returning to the tab always re-asks. That
 * re-ask is what keeps tabs agreeing — signing in or out in one is picked up by the others.
 */
const cacheQueryOptions = {
  staleTime: Infinity,
  refetchOnWindowFocus: 'always',
} as const;

const updateUserGAClientIds = async (user: { gaClientIds?: string[]; _id?: string }) => {
  const clientId = await getClientId();
  const userClientIds = user.gaClientIds ?? [];
  if (!userClientIds.includes(clientId) && user?._id) {
    await patch(buildURL(`/users/${user._id}/clientId`), {
      gaClientIds: [...userClientIds, clientId],
    });
  }
};

/**
 * An ODTP on the landing URL is redeemed by the server as a side effect of reading the user.
 *
 * Captured here rather than in a route component because it has to be in hand before the query
 * fires, and this mounts above the router. It belongs to the URL the visitor arrived on, not to
 * whichever page happens to render first.
 */
const captureOdtp = () => {
  const odtpEntry = Array.from(new URL(document.location.href).searchParams).find(([key]) =>
    key.toLowerCase().includes('odtp'),
  );
  if (odtpEntry) {
    const [key, value] = odtpEntry;
    setPendingOdtpParam(checkLocalStorageForODTP(`${key}=${value}`));
  }
  return null;
};

/**
 * Module-level, because this may be mounted more than once during a hand-over between entry points
 * and both instances observe the same query. Per-instance state would report one automatic login
 * per mount.
 */
let lastAppliedAt = 0;
let hasIdentified = false;

const UserSessionSync = () => {
  const dispatch = useDispatch();
  // A `useState` initialiser, not an effect: effects run after render, by which time the query
  // below has already been issued without the token.
  useState(captureOdtp);

  const { data, dataUpdatedAt, isSuccess, isError } = useQuery({
    ...getUserQueryOptions(),
    ...cacheQueryOptions,
  });

  /**
   * Apply each *new* answer, and only those.
   *
   * Keyed on `dataUpdatedAt` rather than on redux state. Depending on `logged` here meant that
   * signing up inside the Speedy Flow re-ran this against the `null` still cached from page load —
   * `staleTime: Infinity` means the query does not refetch on its own — and signed the new account
   * straight back out, mid-checkout.
   *
   * `data` is `null`, not `undefined`, when nobody is signed in: the endpoint answers 204 and
   * `handleResponse` turns that into an explicit null. That separates "the server says there is no
   * user" from "we have not asked yet", and only the first signs the tab out. A failed request
   * reaches neither, because react-query keeps the last successful data on error.
   */
  useEffect(() => {
    if (isError) {
      dispatch(setSessionError());
      return;
    }
    if (!isSuccess || !dataUpdatedAt || dataUpdatedAt === lastAppliedAt) {
      return;
    }
    lastAppliedAt = dataUpdatedAt;

    if (!data) {
      dispatch(unsetUserData());
      return;
    }
    dispatch(setUserData(data));
    // Identity and the automatic-login event describe arriving, so they happen once — not again
    // every time the visitor comes back to the tab.
    if (!hasIdentified) {
      hasIdentified = true;
      updateUserGAClientIds(data);
      identifyUser(data as { _id: string; role: string });
      trackMP('login', { method: 'automatic' });
    }
  }, [data, dataUpdatedAt, dispatch, isError, isSuccess]);

  return null;
};

export default UserSessionSync;
